import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, links, resolveFormattingLocale } from '@eis/core';
import { DetailsSection, StatusChip } from '@eis/patterns';
import type { BillingApi } from '@eis/billingservice-sdk';
import {
  isBillingUnavailable,
  useCorrectPolicyInvoices,
  useCreateManualInvoice,
  useIssueInvoice,
  usePolicyBilling,
  useScheduleInvoice,
  useUpdatePolicyBillingStatus,
  type InvoiceType,
} from '../api/invoices';
import { formatMinor, invoiceTitle, periodLabel, refusalKeyOf } from '../model/types';

/**
 * The dialogs the billing module owns (SDD-015 §7.1): issue now or for a period, a correction for
 * a period, the schedule row, a manual invoice with free items. The insurance pages open them
 * through `links.billing.invoices({ policyCode, action })` — a link, never an import (backbone
 * §1) — so every dialog takes the policy it is about as a prefilled, editable field.
 *
 * Every refusal is the service's own reason (SDD-015 §6.8), translated under `refusals.<REASON>`
 * and shown in the dialog that caused it; an outage is an outage; nothing is retried. What a
 * dialog produced stays visible in it — the number, the amount, a link to the document — until
 * the operator closes it, because "created" without a way to look at it is a promise.
 */
export type DialogAction = 'issue' | 'period' | 'correction' | 'schedule' | 'manual';

/** The service's refusal, or the outage, in the reader's words. */
export function RefusalAlert({ error }: { readonly error: unknown }): ReactNode {
  const { t } = useTranslation('billing');
  if (error === null || error === undefined) return null;
  if (isBillingUnavailable(error)) {
    return <Alert severity="error" data-testid="billing-unavailable">{t('refusals.unavailable')}</Alert>;
  }
  const key = refusalKeyOf(error);
  const message =
    key === undefined
      ? t('refusals.generic')
      : t(`refusals.${key}` as never, { defaultValue: t('refusals.generic') });
  return (
    <Alert severity="warning" data-testid="billing-refusal">
      {message}
    </Alert>
  );
}

/** "Rechnung INV-… erstellt: 8,59 €" with the document one click away. */
function IssuedAlert({
  invoice,
  message,
}: {
  readonly invoice: BillingApi.Invoice;
  readonly message: string;
}): ReactNode {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const code = invoice.code ?? '';
  return (
    <Alert severity="success" data-testid="billing-issued">
      {message}{' '}
      <Link
        component="button"
        type="button"
        onClick={() => {
          void navigate({ href: links.billing.invoice(code) });
        }}
      >
        {t('dialogs.open')}
      </Link>
    </Alert>
  );
}

const isCivilDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

function PolicyField({
  value,
  onChange,
  error,
  disabled,
}: {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error: boolean;
  readonly disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('billing');
  return (
    <TextField
      size="small"
      fullWidth
      required
      label={t('dialogs.policy')}
      value={value}
      error={error}
      helperText={error ? t('dialogs.required') : t('dialogs.policyHint')}
      disabled={disabled}
      onChange={(event) => {
        onChange(event.target.value.trim());
      }}
      slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
    />
  );
}

function PeriodFields({
  from,
  to,
  onFrom,
  onTo,
  submitted,
}: {
  readonly from: string;
  readonly to: string;
  readonly onFrom: (next: string) => void;
  readonly onTo: (next: string) => void;
  readonly submitted: boolean;
}): ReactNode {
  const { t } = useTranslation('billing');
  const fromMissing = submitted && !isCivilDate(from);
  const toMissing = submitted && !isCivilDate(to);
  return (
    <Stack direction="row" spacing={2}>
      {/* A billing period is a CIVIL range (§5.3), not an effective date: plain date inputs, no picker. */}
      <TextField
        size="small"
        fullWidth
        required
        type="date"
        label={t('dialogs.from')}
        value={from}
        error={fromMissing}
        helperText={fromMissing ? t('dialogs.required') : ' '}
        onChange={(event) => {
          onFrom(event.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        size="small"
        fullWidth
        required
        type="date"
        label={t('dialogs.to')}
        value={to}
        error={toMissing}
        helperText={toMissing ? t('dialogs.required') : ' '}
        onChange={(event) => {
          onTo(event.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------------------------
// Issue now / for a period
// ---------------------------------------------------------------------------------------------

export function IssueInvoiceDialog({
  open,
  onClose,
  policyCode: initialPolicyCode = '',
  explicitPeriod = false,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly policyCode?: string;
  /** Opened as "invoice for a period" (§7.1): the period fields start visible. */
  readonly explicitPeriod?: boolean;
}): ReactNode {
  const { t, i18n } = useTranslation('billing');
  const locale = resolveFormattingLocale(i18n.language);
  const issue = useIssueInvoice();
  const [policyCode, setPolicyCode] = useState(initialPolicyCode);
  const [mode, setMode] = useState<'next' | 'period'>(explicitPeriod ? 'period' : 'next');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<{ invoice?: BillingApi.Invoice; noChange: boolean } | null>(null);

  const close = (): void => {
    setSubmitted(false);
    setResult(null);
    issue.reset();
    onClose();
  };

  const submit = (): void => {
    setSubmitted(true);
    if (policyCode === '') return;
    if (mode === 'period' && (!isCivilDate(from) || !isCivilDate(to))) return;
    void issue
      .mutateAsync({
        policyCode,
        // The service types the first document `initial` and every later one `recurring` itself;
        // the legacy path is the recurring one, and a wrong "initial" would be refused (§6.1).
        kind: 'recurring',
        ...(mode === 'period' ? { billingIntervalFrom: from, billingIntervalTo: to } : {}),
        ...(isCivilDate(dueDate) ? { dueDate } : {}),
      })
      .then((response) => {
        setResult({ invoice: response.invoice, noChange: response.noChange });
      })
      .catch(() => undefined); // the refusal renders from `issue.error`
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('dialogs.issue.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('dialogs.issue.description')}
          </Typography>
          <PolicyField value={policyCode} onChange={setPolicyCode} error={submitted && policyCode === ''} />
          <RadioGroup
            value={mode}
            onChange={(_event, next) => {
              setMode(next === 'period' ? 'period' : 'next');
            }}
          >
            <FormControlLabel value="next" control={<Radio size="small" />} label={t('dialogs.issue.nextPeriod')} />
            <FormControlLabel value="period" control={<Radio size="small" />} label={t('dialogs.issue.explicitPeriod')} />
          </RadioGroup>
          {mode === 'period' && <PeriodFields from={from} to={to} onFrom={setFrom} onTo={setTo} submitted={submitted} />}
          <TextField
            size="small"
            fullWidth
            type="date"
            label={t('dialogs.dueDate')}
            value={dueDate}
            onChange={(event) => {
              setDueDate(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <RefusalAlert error={issue.error} />
          {result?.noChange === true && <Alert severity="info" data-testid="billing-no-change">{t('dialogs.issue.noChange')}</Alert>}
          {result?.invoice !== undefined && (
            <IssuedAlert
              invoice={result.invoice}
              message={t('dialogs.issue.issued', {
                number: invoiceTitle(result.invoice),
                amount: formatMinor(result.invoice.grossAmount, result.invoice.currency, locale),
              })}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result === null ? t('dialogs.cancel') : t('dialogs.close')}</Button>
        <Button variant="contained" onClick={submit} disabled={issue.isPending || result?.invoice !== undefined}>
          {t('dialogs.issue.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Correction for a period
// ---------------------------------------------------------------------------------------------

export function CorrectionDialog({
  open,
  onClose,
  policyCode: initialPolicyCode = '',
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly policyCode?: string;
}): ReactNode {
  const { t, i18n } = useTranslation('billing');
  const locale = resolveFormattingLocale(i18n.language);
  const correct = useCorrectPolicyInvoices();
  const [policyCode, setPolicyCode] = useState(initialPolicyCode);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<BillingApi.CreateCorrectionInvoicesResponse | null>(null);

  const close = (): void => {
    setSubmitted(false);
    setResult(null);
    correct.reset();
    onClose();
  };

  const submit = (): void => {
    setSubmitted(true);
    if (policyCode === '' || !isCivilDate(from) || !isCivilDate(to)) return;
    void correct
      .mutateAsync({ policyCode, billingIntervalFrom: from, billingIntervalTo: to })
      .then(setResult)
      .catch(() => undefined);
  };

  const issued = result?.invoices ?? [];
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('dialogs.correction.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('dialogs.correction.description')}
          </Typography>
          <PolicyField value={policyCode} onChange={setPolicyCode} error={submitted && policyCode === ''} />
          <PeriodFields from={from} to={to} onFrom={setFrom} onTo={setTo} submitted={submitted} />
          <RefusalAlert error={correct.error} />
          {result?.noChange === true && <Alert severity="info" data-testid="billing-no-change">{t('dialogs.correction.noChange')}</Alert>}
          {issued.map((invoice) => (
            <IssuedAlert
              key={invoice.code}
              invoice={invoice}
              message={t('dialogs.correction.issued', {
                number: invoiceTitle(invoice),
                amount: formatMinor(invoice.grossAmount, invoice.currency, locale),
              })}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result === null ? t('dialogs.cancel') : t('dialogs.close')}</Button>
        <Button variant="contained" onClick={submit} disabled={correct.isPending || issued.length > 0}>
          {t('dialogs.correction.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// The schedule row (B-10): put on the plan, pause, resume.
// ---------------------------------------------------------------------------------------------

function ScheduleRowView({
  policyCode,
  locale,
}: {
  readonly policyCode: string;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('billing');
  const row = usePolicyBilling(policyCode);
  const schedule = useScheduleInvoice();
  const update = useUpdatePolicyBillingStatus();

  if (row.isPending) return <Skeleton variant="rounded" height={120} />;
  if (row.isError) return <RefusalAlert error={row.error} />;

  const billing = row.data?.policyBilling;
  const version = row.data?.version?.version ?? '';
  if (billing === undefined) {
    return (
      <Stack spacing={2}>
        <Alert severity="info">{t('dialogs.schedule.none')}</Alert>
        <RefusalAlert error={schedule.error} />
        <Button
          variant="contained"
          disabled={schedule.isPending}
          onClick={() => {
            schedule.mutate({ policyCode });
          }}
        >
          {t('dialogs.schedule.put')}
        </Button>
      </Stack>
    );
  }

  const status = billing.status ?? '';
  const viaMaster = status === 'via_master';
  const nextDate = (billing.nextBillingDate ?? '').slice(0, 10);
  return (
    <Stack spacing={2}>
      <DetailsSection
        title={t('dialogs.schedule.title')}
        fields={[
          {
            label: t('dialogs.schedule.mode'),
            value: t(`schedule.mode.${billing.billingMode ?? ''}` as never, { defaultValue: billing.billingMode ?? '—' }),
          },
          {
            label: t('dialogs.schedule.status'),
            value: <StatusChip status={status} label={t(`schedule.status.${status}` as never, { defaultValue: status })} />,
          },
          {
            label: t('dialogs.schedule.nextDate'),
            value: nextDate === '' ? t('dialogs.schedule.noDate') : formatCivilDate(nextDate, locale),
          },
          {
            label: t('dialogs.schedule.nextPeriod'),
            value: periodLabel(billing.nextPeriodFrom ?? '', billing.nextPeriodTo ?? '', locale),
          },
          {
            label: t('dialogs.schedule.lastInvoiced'),
            // A row that never invoiced has no date; the staged schedule example spells that as
            // `null`, the service as an absent field — both are "none", never a date to format.
            value:
              typeof billing.lastInvoicedPeriodTo !== 'string' || billing.lastInvoicedPeriodTo === ''
                ? t('dialogs.schedule.noDate')
                : formatCivilDate(billing.lastInvoicedPeriodTo, locale),
          },
        ]}
      />
      {viaMaster && (
        <Alert severity="info">{t('dialogs.schedule.viaMaster', { master: billing.groupPolicyCode ?? '' })}</Alert>
      )}
      <RefusalAlert error={update.error} />
      {(status === 'pending' || status === 'paused') && (
        <Button
          variant="outlined"
          disabled={update.isPending}
          onClick={() => {
            update.mutate({
              policyCode,
              code: billing.code ?? '',
              status: status === 'pending' ? 'paused' : 'pending',
              expectedVersion: version,
            });
          }}
        >
          {status === 'pending' ? t('dialogs.schedule.pause') : t('dialogs.schedule.resume')}
        </Button>
      )}
    </Stack>
  );
}

export function ScheduleDialog({
  open,
  onClose,
  policyCode: initialPolicyCode = '',
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly policyCode?: string;
}): ReactNode {
  const { t, i18n } = useTranslation('billing');
  const locale = resolveFormattingLocale(i18n.language);
  const [policyCode, setPolicyCode] = useState(initialPolicyCode);
  const [shown, setShown] = useState(initialPolicyCode);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('dialogs.schedule.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('dialogs.schedule.description')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <PolicyField value={policyCode} onChange={setPolicyCode} error={false} />
            <Button
              variant="outlined"
              disabled={policyCode === ''}
              onClick={() => {
                setShown(policyCode);
              }}
              sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
            >
              {t('dialogs.schedule.load')}
            </Button>
          </Stack>
          {shown !== '' && <ScheduleRowView key={shown} policyCode={shown} locale={locale} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('dialogs.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// A manual invoice with free items
// ---------------------------------------------------------------------------------------------

interface ItemDraft {
  readonly name: string;
  readonly quantity: string;
  readonly pricePerUnit: string;
}

const MANUAL_TYPES: readonly Exclude<InvoiceType, 'estimated' | 'penalty'>[] = [
  'other',
  'initial',
  'recurring',
  'correction',
  'final',
  'withdraw',
];

const EMPTY_ITEM: ItemDraft = { name: '', quantity: '1', pricePerUnit: '' };

export function ManualInvoiceDialog({
  open,
  onClose,
  policyCode: initialPolicyCode = '',
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly policyCode?: string;
}): ReactNode {
  const { t, i18n } = useTranslation('billing');
  const locale = resolveFormattingLocale(i18n.language);
  const create = useCreateManualInvoice();
  const [policyCode, setPolicyCode] = useState(initialPolicyCode);
  const [type, setType] = useState<(typeof MANUAL_TYPES)[number]>('other');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [items, setItems] = useState<readonly ItemDraft[]>([EMPTY_ITEM]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<BillingApi.Invoice | null>(null);

  const parsedItems = items.map((item) => ({
    name: item.name.trim(),
    quantity: Number(item.quantity.replace(',', '.')),
    pricePerUnit: Number(item.pricePerUnit.replace(',', '.')),
  }));
  const itemsValid =
    parsedItems.length > 0 &&
    parsedItems.every((item) => item.name !== '' && Number.isFinite(item.quantity) && Number.isFinite(item.pricePerUnit) && item.pricePerUnit !== 0 && item.quantity !== 0);

  const close = (): void => {
    setSubmitted(false);
    setResult(null);
    create.reset();
    onClose();
  };

  const submit = (): void => {
    setSubmitted(true);
    if (policyCode === '' || !isCivilDate(from) || !isCivilDate(to) || !itemsValid) return;
    void create
      .mutateAsync({
        policyCode,
        type,
        billingIntervalFrom: from,
        billingIntervalTo: to,
        ...(isCivilDate(dueDate) ? { dueDate } : {}),
        items: parsedItems,
      })
      .then((response) => {
        setResult(response.invoice ?? null);
      })
      .catch(() => undefined);
  };

  const setItem = (index: number, patch: Partial<ItemDraft>): void => {
    setItems((current) => current.map((item, at) => (at === index ? { ...item, ...patch } : item)));
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>{t('dialogs.manual.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('dialogs.manual.description')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <PolicyField value={policyCode} onChange={setPolicyCode} error={submitted && policyCode === ''} />
            <TextField
              select
              size="small"
              label={t('dialogs.manual.type')}
              value={type}
              onChange={(event) => {
                setType(event.target.value as (typeof MANUAL_TYPES)[number]);
              }}
              sx={{ minWidth: 200 }}
            >
              {MANUAL_TYPES.map((candidate) => (
                <MenuItem key={candidate} value={candidate}>
                  {t(`invoices.type.${candidate}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <PeriodFields from={from} to={to} onFrom={setFrom} onTo={setTo} submitted={submitted} />
          <TextField
            size="small"
            type="date"
            label={t('dialogs.dueDate')}
            value={dueDate}
            onChange={(event) => {
              setDueDate(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: 260 }}
          />
          <Typography variant="subtitle2" component="h3">
            {t('dialogs.manual.items')}
          </Typography>
          {items.map((item, index) => (
            <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <TextField
                size="small"
                fullWidth
                required
                label={t('dialogs.manual.itemName')}
                value={item.name}
                error={submitted && item.name.trim() === ''}
                onChange={(event) => {
                  setItem(index, { name: event.target.value });
                }}
              />
              <TextField
                size="small"
                required
                label={t('dialogs.manual.quantity')}
                value={item.quantity}
                error={submitted && !Number.isFinite(Number(item.quantity.replace(',', '.')))}
                onChange={(event) => {
                  setItem(index, { quantity: event.target.value });
                }}
                sx={{ width: 120 }}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              <TextField
                size="small"
                required
                label={t('dialogs.manual.pricePerUnit', { currency: 'EUR' })}
                value={item.pricePerUnit}
                error={submitted && (item.pricePerUnit === '' || !Number.isFinite(Number(item.pricePerUnit.replace(',', '.'))))}
                onChange={(event) => {
                  setItem(index, { pricePerUnit: event.target.value });
                }}
                sx={{ width: 200 }}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              <IconButton
                aria-label={t('dialogs.manual.removeItem')}
                disabled={items.length === 1}
                onClick={() => {
                  setItems((current) => current.filter((_item, at) => at !== index));
                }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setItems((current) => [...current, EMPTY_ITEM]);
            }}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('dialogs.manual.addItem')}
          </Button>
          <RefusalAlert error={create.error} />
          {result !== null && (
            <IssuedAlert
              invoice={result}
              message={t('dialogs.manual.created', {
                number: invoiceTitle(result),
                amount: formatMinor(result.grossAmount, result.currency, locale),
              })}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result === null ? t('dialogs.cancel') : t('dialogs.close')}</Button>
        <Button variant="contained" onClick={submit} disabled={create.isPending || result !== null}>
          {t('dialogs.manual.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The one dialog a route's `action` names, so a page mounts exactly the one the link asked for. */
export function BillingActionDialog({
  action,
  policyCode,
  onClose,
}: {
  readonly action: DialogAction | null;
  readonly policyCode: string;
  readonly onClose: () => void;
}): ReactNode {
  switch (action) {
    case 'issue':
      return <IssueInvoiceDialog open onClose={onClose} policyCode={policyCode} />;
    case 'period':
      return <IssueInvoiceDialog open onClose={onClose} policyCode={policyCode} explicitPeriod />;
    case 'correction':
      return <CorrectionDialog open onClose={onClose} policyCode={policyCode} />;
    case 'schedule':
      return <ScheduleDialog open onClose={onClose} policyCode={policyCode} />;
    case 'manual':
      return <ManualInvoiceDialog open onClose={onClose} policyCode={policyCode} />;
    case null:
      return null;
  }
}
